import { Injectable } from '@angular/core';
import { InventoryVehiclesService } from '../../shared/services/inventory-vehicles.service';
import { Action, Store } from '@ngrx/store';
import { Observable } from 'rxjs/internal/Observable';
import * as fromActions from './vehicle.actions';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import { ofType, Actions, createEffect } from '@ngrx/effects';
import { of } from 'rxjs/internal/observable/of';
import { AlertService } from '../../shared/services/alert.service';
import { IResponse, ITestDriveVehicleInventory, ISyncStatus, SourceSystem } from '@signal/asp-data-commons';
import { toData } from '../app.adapter';
import { BdcDashboardState } from '../bdcdashboard/bdcdashboard.reducer';
import { LoadPendingAppointments } from '../bdcdashboard/bdcdashboard.actions';
import { getAlertErrorMessage } from '../../shared/services/util.service';


@Injectable()
export class VehiclesEffects {
    somethingWentWrongPleaseReloadThePage = 'otherTexts.somethingWentWrongPleaseReloadThePage';
    constructor(
        private readonly actions$: Actions,
        private readonly inventoryVehiclesService: InventoryVehiclesService,
        private readonly alertService: AlertService,
        private readonly BdcDashboardStateStore: Store<BdcDashboardState>
    ) {
    }

    addVehicle: Observable<Action> = createEffect(() => {
        return this.actions$.pipe(
            ofType<fromActions.AddVehicle>(
                fromActions.VehiclesActionTypes.ADD_VEHICLE
            ),
            mergeMap((action: fromActions.AddVehicle) =>
                this.inventoryVehiclesService.AddNewVehicle(action.payload.vehicle).pipe(
                    switchMap((vehicle: IResponse<ITestDriveVehicleInventory>) => toData(vehicle, 201)),
                    map((vehicle: ITestDriveVehicleInventory) => {
                        this.alertService.showAlert(`Vehicle details for VIN ${vehicle.vin} is added succesfully`, '', '', 5000, false);
                       return new fromActions.AddVehicleSuccess({ vehicle })
                    }),
                    catchError(err => {
                    this.alertService.showAlert(err?.error?.status?.messages[0] || this.somethingWentWrongPleaseReloadThePage);
                    return of(new fromActions.AddVehicleFailure({ error: err }))
            } )
                )
            )
        );
    })
    
    loadAllVehicles: Observable<Action> = createEffect(() => {
        return this.actions$.pipe(
            ofType<fromActions.LoadVehicles>(
                fromActions.VehiclesActionTypes.LOAD_VEHICLES
            ),
            mergeMap((action: fromActions.LoadVehicles) =>
                this.inventoryVehiclesService.getAllVehicles(action.payload.offset, action.payload.limit, false).pipe(
                    switchMap((vehicles: IResponse<any>) => toData(vehicles)),
                    map((vehicles: any) => {
                       return new fromActions.LoadVehiclesSuccess({ vehicles })
                    }),
                    catchError(err => {
                    this.alertService.showAlert(err?.error?.status?.messages[0] || this.somethingWentWrongPleaseReloadThePage);
                    return of(new fromActions.LoadVehiclesFailure({ error: err }))
            } )
                )
            )
        );
    })

    loadAllVehiclesFromInventory: Observable<Action> = createEffect(() => {
        return this.actions$.pipe(
            ofType<fromActions.LoadAllVehicles>(
                fromActions.VehiclesActionTypes.LOAD_ALL_VEHICLES
            ),
            mergeMap((action: fromActions.LoadAllVehicles) =>
                this.inventoryVehiclesService.getAllVehiclesFromFleetIventory(action.payload.view).pipe(
                    switchMap((vehicles: IResponse<any>) => toData(vehicles)),
                    map((vehicles: any) => {
                       return new fromActions.LoadAllVehiclesSuccess({ vehicles : action.payload.view === SourceSystem.ME ? vehicles : vehicles.vehicles })
                    }),
                    catchError(err => {
                    this.alertService.showAlert(err?.error?.status?.messages[0] || this.somethingWentWrongPleaseReloadThePage);
                    return of(new fromActions.LoadAllVehiclesFailure({ error: err }))
            } )
                )
            )
        );
    })

    updateVehicle: Observable<Action> = createEffect(() => {
        return this.actions$.pipe(
            ofType<fromActions.UpdateVehicle>(
                fromActions.VehiclesActionTypes.UPDATE_VEHICLE
            ),
            mergeMap((action: fromActions.UpdateVehicle) =>
                this.inventoryVehiclesService.UpdateVehicle(action.payload.vehicle).pipe(
                    switchMap((vehicle: IResponse<ITestDriveVehicleInventory>) => toData(vehicle)),
                    map((vehicle: ITestDriveVehicleInventory) => {
                        if(action.payload?.isLoadNotification) {
                            this.BdcDashboardStateStore.dispatch(new LoadPendingAppointments({ type : 'deletePending'}));
                        }
                        this.alertService.showAlert(`Vehicle details for VIN ${vehicle.vin} is updated succesfully`, '', '', 5000, false);
                       return new fromActions.UpdateVehicleSuccess({ vehicle })
                    }),
                    catchError(err => {
                    this.alertService.showAlert(err?.error?.status?.messages[0] || this.somethingWentWrongPleaseReloadThePage);
                    return of(new fromActions.UpdateVehicleFailure({ error: err }))
            } )
                )
            )
        );
    })

    updateNviStatus: Observable<Action> = createEffect(() => {
        return this.actions$.pipe(
            ofType<fromActions.UpdateNVIStatus>(
                fromActions.VehiclesActionTypes.UPDATE_NVI_STATUS
            ),
            mergeMap((action: fromActions.UpdateNVIStatus) =>
                this.inventoryVehiclesService.updateNviStatus(action.payload.vehicle.unitId,action.payload.vehicle.status).pipe(
                    switchMap((data: any) => toData(data)),
                    map((resp: any) => {
                        const {vehicle} : any= action.payload
                        if(resp?.status === 'updated'){
                            if(vehicle?.nextTestDriveDate) {
                                this.BdcDashboardStateStore.dispatch(new LoadPendingAppointments({ type : 'deletePending'}));
                            }
                            this.alertService.showAlert(`Vehicle details for VIN ${vehicle.vin} is updated succesfully`, '', '', 5000, false);
                        }else{
                            vehicle.status = !action.payload.vehicle.status
                            this.alertService.showAlert(`Vehicle details for VIN ${vehicle.vin} could not be updated`);
                        }
                        // To refresh the delete pending count for the navigation warning
                        if(!!(vehicle?.nextTestDriveDate && vehicle?.expiredAt)) {
                            this.BdcDashboardStateStore.dispatch(new LoadPendingAppointments({ type : 'deletePending'}));
                        }
                       return new fromActions.UpdateNVIStatusSuccess({ vehicle })
                    }),
                    catchError(err => {
                    this.alertService.showAlert(err?.error?.status?.messages[0] || this.somethingWentWrongPleaseReloadThePage);
                    return of(new fromActions.UpdateNVIStatusFailure({ error: err }))
            } )
                )
            )
        );
    })

    updateUVIStatus: Observable<Action> = createEffect(() => {
        return this.actions$.pipe(
            ofType<fromActions.UpdateUVIStatus>(
                fromActions.VehiclesActionTypes.UPDATE_UVI_STATUS
            ),
            mergeMap((action: fromActions.UpdateUVIStatus) =>
                this.inventoryVehiclesService.updateUVIStatus(action.payload.vehicle.vin,action.payload.vehicle.status).pipe(
                    switchMap((data: any) => toData(data)),
                    map((resp: any) => {
                        const {vehicle}: any = action.payload
                        if(resp?.status === 'updated'){
                            if(vehicle?.nextTestDriveDate) {
                                this.BdcDashboardStateStore.dispatch(new LoadPendingAppointments({ type : 'deletePending'}));
                            }
                            this.alertService.showAlert(`Vehicle details for VIN ${vehicle.vin} updated succesfully`, '', '', 5000, false);
                        }else{
                            vehicle.status = !action.payload.vehicle.status
                            this.alertService.showAlert(`Vehicle details for VIN ${vehicle.vin} could not be updated`);
                        }
                        // To refresh the delete pending count for the navigation warning
                        if(!!(vehicle?.nextTestDriveDate && vehicle?.expiredAt)) {
                            this.BdcDashboardStateStore.dispatch(new LoadPendingAppointments({ type : 'deletePending'}));
                        }
                       return new fromActions.UpdateUVIStatusSuccess({ vehicle })
                    }),
                    catchError(err => {
                    this.alertService.showAlert(err?.error?.status?.messages[0] || this.somethingWentWrongPleaseReloadThePage);
                    return of(new fromActions.UpdateUVIStatusFailure({ error: err }))
            } )
                )
            )
        );
    })

    updateBulkVehicleStatus: Observable<Action> = createEffect(() => {
        return this.actions$.pipe(
            ofType<fromActions.UpdateBulkVehicleStatus>(
                fromActions.VehiclesActionTypes.UPDATE_BULK_VEHICLE_STATUS
            ),
            mergeMap((action: fromActions.UpdateBulkVehicleStatus) =>
                this.inventoryVehiclesService.updateBulkVehicleStatus(action.payload.payload, action.payload.status, action.payload.source).pipe(
                    switchMap((data: IResponse<any>) => toData(data, 201)),
                    map((data: any) => {
                        this.alertService.showAlert(action.payload.status ? 'Selected vehicles are now online' : 'Selected vehicles are now offline', '', '', 5000, false);
                        if (action.payload.requestedVehicles.some(vehicle => vehicle?.nextTestDriveDate || vehicle?.expiredAt)) {
                            this.BdcDashboardStateStore.dispatch(new LoadPendingAppointments({ type : 'deletePending'}));
                        }
                        return new fromActions.UpdateBulkVehicleStatusSuccess({
                            requestedVehicles: action.payload.requestedVehicles,
                            records: data?.records || []
                        });
                    }),
                    catchError(err => {
                        this.alertService.showAlert(err?.error?.status?.messages[0] || this.somethingWentWrongPleaseReloadThePage);
                        return of(new fromActions.UpdateBulkVehicleStatusFailure({ error: err }));
                    })
                )
            )
        );
    })
    
    deleteVehicle: Observable<Action> = createEffect(() => {
        return this.actions$.pipe(
            ofType<fromActions.DeleteVehicle>(
                fromActions.VehiclesActionTypes.DELETE_VEHICLE
            ),
            mergeMap((action: fromActions.DeleteVehicle) =>
                this.inventoryVehiclesService.deleteVehicle(action.payload.vehicles).pipe(
                    switchMap((vehicle: IResponse<any>) => toData(vehicle)),
                    map((vehicles: any) => {
                        this.alertService.showAlert(action.payload.vehicles.length > 1 ? 'vehicleManagement.theSelectedVehiclesAreDeletedSuccessfully':
                        'vehicleManagement.theSelectedVehicleIsDeletedSuccessfully', '', '', 5000, false);
                        this.BdcDashboardStateStore.dispatch(new LoadPendingAppointments({ type : 'deletePending'}));
                       return new fromActions.DeleteVehicleSuccess({ requestedVehicles:action.payload.vehicles, response: vehicles })
                    }),
                    catchError(err => {
                    this.alertService.showAlert(err?.error?.status?.messages[0] || this.somethingWentWrongPleaseReloadThePage);
                    return of(new fromActions.DeleteVehicleFailure({ error: err }))
            } )
                )
            )
        );
    })

    loadSyncStatus: Observable<Action> = createEffect(() => {
        return this.actions$.pipe(
            ofType<fromActions.LoadSyncStatus>(
            fromActions.VehiclesActionTypes.LOAD_SYNC_STATUS
            ),
            mergeMap((action: fromActions.LoadSyncStatus) =>
            this.inventoryVehiclesService.getVehicleInvnetorySyncStatus().pipe(
                switchMap((status: IResponse<ISyncStatus>) => toData(status)),
                map((status: ISyncStatus) => new fromActions.LoadSyncStatusSuccess({ status })),
                catchError(err => {
                const errMsg = getAlertErrorMessage(err);
                this.alertService.showAlert(errMsg);
                return of(new fromActions.LoadSyncStatusFailure({ error: err }))
                })
            )
            )
        );
    })

    updateFromCrm: Observable<Action> = createEffect(() => {
        return this.actions$.pipe(
            ofType<fromActions.UpdateFromCrm>(
                fromActions.VehiclesActionTypes.UPDATE_FROM_CRM
            ),
            mergeMap((action: fromActions.UpdateFromCrm) => {
            return this.inventoryVehiclesService.updateVehicleInventoryFromCrm().pipe(
                switchMap((status: IResponse<ISyncStatus>) => toData(status, 200)),
                map(
                (status: ISyncStatus) => {
                    this.alertService.showAlert(`otherTexts.crmSyncInitiatedSuccessfully`, '', '', 5000, false);
                    return new fromActions.UpdateFromCrmSuccess({ status })
                },
                ),
                catchError(err => {
                    this.alertService.showAlert('otherTexts.somethingWentWrongPleaseTryAgain');
                    return of(new fromActions.UpdateFromCrmFailure({ error: err }))
                })
            )
            }
        )
        );
    })

  restoreVehicle: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
        ofType<fromActions.RestoreVehicle>(
            fromActions.VehiclesActionTypes.RESTORE_VEHICLE
        ),
        mergeMap((action: fromActions.RestoreVehicle) =>
            this.inventoryVehiclesService.restoreVehicle(action.payload.vehicles).pipe(
                switchMap((vehicle: IResponse<any>) => toData(vehicle)),
                map((vehicles: any) => {
                    this.alertService.showAlert(action.payload.vehicles.length > 1 ? 'vehicleManagement.selectedVehiclesAreRestoredSuccessfully':
                    'vehicleManagement.selectedVehicleIsRestoredSuccessfully', '', '', 5000, false);
                    this.BdcDashboardStateStore.dispatch(new LoadPendingAppointments({ type : 'deletePending'}));
                   return new fromActions.RestoreVehicleSuccess({ vehicles: vehicles })
                }),
                catchError(err => {
                this.alertService.showAlert(err?.error?.status?.messages[0] || this.somethingWentWrongPleaseReloadThePage);
                return of(new fromActions.DeleteVehicleFailure({ error: err }))
        } )
            )
        )
    );
  })

  fetchVehiclesBasedOnSource: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
        ofType<fromActions.FetchVehiclesBasedOnSource>(
            fromActions.VehiclesActionTypes.FETCH_VEHICLES_FROM_SOURCE
        ),
        mergeMap((action: fromActions.FetchVehiclesBasedOnSource) =>
            this.inventoryVehiclesService.getVehiclesFromSource(action?.payload?.source).pipe(
                switchMap((vehicles: IResponse<any>) => toData(vehicles)),
                map((vehicles: any) => { 
                   return new fromActions.FetchVehiclesBasedOnSourceSuccess({ vehicles : vehicles.vehicles })
                }),
                catchError(err => {
                this.alertService.showAlert(err?.error?.status?.messages[0] || this.somethingWentWrongPleaseReloadThePage);
                return of(new fromActions.FetchVehiclesBasedOnSourceFaliure({ error: err }))
        } )
            )
        )
    );
  });
}